import { useState, useMemo, useEffect, useRef } from 'react';
import { Plus, Loader2, Calendar as CalendarIcon, Info, X, Wand2, Trash2 } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { useAppointments } from '../hooks/useAppointments';
import { MonthView } from '../components/MonthView';
import { AppointmentCard } from '../components/AppointmentCard';
import { AddAppointmentModal } from '../components/AddAppointmentModal';
import { AppointmentDetailModal } from '../components/AppointmentDetailModal';
import { AppointmentSetupWizard, type AppointmentToCreate } from '../components/AppointmentSetupWizard';
import type { Appointment } from '../types';

const WIZARD_SHOWN_KEY = 'parentpath_wizard_shown';

// Helper function to format date as YYYY-MM-DD using local timezone
const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export function CalendarPage() {
  const { t, i18n } = useTranslation('calendar');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [showCheckupsAddedInfo, setShowCheckupsAddedInfo] = useState(false);
  const [showSetupWizard, setShowSetupWizard] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showWizardPrompt, setShowWizardPrompt] = useState(false);
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const hasCheckedFirstVisit = useRef(false);

  const {
    appointments,
    isLoading,
    error,
    addAppointment,
    addMultipleAppointments,
    updateAppointment,
    deleteAppointment,
    deleteAllAppointments,
  } = useAppointments();

  // Auto-open wizard on first visit when no appointments exist
  useEffect(() => {
    if (!isLoading && !hasCheckedFirstVisit.current) {
      hasCheckedFirstVisit.current = true;
      const wizardShown = localStorage.getItem(WIZARD_SHOWN_KEY);
      if (!wizardShown && appointments.length === 0) {
        setShowSetupWizard(true);
        localStorage.setItem(WIZARD_SHOWN_KEY, 'true');
      }
    }
  }, [isLoading, appointments.length]);

  // Filter appointments for selected date or upcoming
  const displayedAppointments = useMemo(() => {
    if (selectedDate) {
      const selectedDateStr = formatLocalDate(selectedDate);
      return appointments.filter((apt) => apt.appointment_date === selectedDateStr);
    }
    // Show upcoming appointments
    const today = formatLocalDate(new Date());
    return appointments
      .filter((apt) => apt.appointment_date >= today)
      .slice(0, 10);
  }, [appointments, selectedDate]);

  const handleMonthChange = (direction: 'prev' | 'next') => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
      return newDate;
    });
  };

  const handleTodayClick = () => {
    const today = new Date();
    setCurrentDate(today);
    setSelectedDate(today);
  };

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
  };

  const handleWizardComplete = async (newAppointments: AppointmentToCreate[]): Promise<boolean> => {
    const success = await addMultipleAppointments(newAppointments);
    if (success) {
      setShowCheckupsAddedInfo(true);
    }
    return success;
  };

  const handleDeleteAll = async () => {
    setIsDeletingAll(true);
    const success = await deleteAllAppointments();
    setIsDeletingAll(false);
    if (success) {
      setShowDeleteConfirm(false);
      setShowWizardPrompt(true);
    }
  };

  const handleWizardPromptYes = () => {
    setShowWizardPrompt(false);
    setShowSetupWizard(true);
  };

  const handleWizardPromptNo = () => {
    setShowWizardPrompt(false);
  };

  const formatSelectedDate = (date: Date) => {
    return date.toLocaleDateString(i18n.language === 'en' ? 'en-GB' : 'de-DE', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-white text-2xl font-semibold">{t('pageTitle')}</h1>
        <div className="flex gap-2">
          <button
            onClick={() => setShowSetupWizard(true)}
            className="glass-effect px-3 py-2 rounded-xl flex items-center gap-1.5 text-white text-sm font-medium"
          >
            <Wand2 className="w-4 h-4" />
            {t('wizard')}
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-pp-orange px-3 py-2 rounded-xl flex items-center gap-1.5 text-white text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            {t('addAppointment')}
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="glass-effect rounded-[15px] p-4 mb-4 border-red-500/50">
          <p className="text-red-300 text-sm">{error}</p>
        </div>
      )}

      {/* Info after adding predefined checkups */}
      {showCheckupsAddedInfo && (
        <div className="glass-effect rounded-[15px] p-4 mb-4" style={{ background: 'rgba(245, 158, 17, 0.2)' }}>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-pp-orange rounded-full flex items-center justify-center flex-shrink-0">
              <Info className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-white">
                {t('checkupsAdded.title')}
              </h3>
              <p className="text-sm text-white/70 mt-1">
                {t('checkupsAdded.description')}
              </p>
              <p className="text-sm text-white/50 mt-2">
                {t('checkupsAdded.tip')}
              </p>
            </div>
            <button
              onClick={() => setShowCheckupsAddedInfo(false)}
              className="text-white/50 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Month View */}
      <MonthView
        currentDate={currentDate}
        selectedDate={selectedDate}
        appointments={appointments}
        onDateSelect={handleDateSelect}
        onMonthChange={handleMonthChange}
        onTodayClick={handleTodayClick}
      />

      {/* Appointments List */}
      <div className="mt-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-white">
            {selectedDate
              ? formatSelectedDate(selectedDate)
              : t('upcomingAppointments')}
          </h2>
          {selectedDate && (
            <button
              onClick={() => setSelectedDate(null)}
              className="text-sm text-pp-orange hover:text-pp-orange/80"
            >
              {t('showAll')}
            </button>
          )}
        </div>

        {displayedAppointments.length === 0 ? (
          <div className="rounded-[15px] p-8 text-center">
            <CalendarIcon className="w-12 h-12 text-white/30 mx-auto mb-3" />
            <p className="text-white/70">
              {selectedDate
                ? t('noAppointmentsDay')
                : t('noAppointmentsUpcoming')}
            </p>
            <button
              onClick={() => setShowAddModal(true)}
              className="mt-4 text-pp-orange text-sm font-medium flex items-center gap-1 mx-auto"
            >
              <Plus className="w-4 h-4" />
              {t('addAppointmentButton')}
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {displayedAppointments.map((appointment) => (
              <AppointmentCard
                key={appointment.id}
                appointment={appointment}
                onClick={() => setSelectedAppointment(appointment)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Delete All Button */}
      {appointments.length > 0 && (
        <div className="mt-6 pt-4 border-t border-white/10">
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="w-full py-3 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            {t('deleteAllButton')}
          </button>
        </div>
      )}

      {/* Add Appointment Modal */}
      <AddAppointmentModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={addAppointment}
        initialDate={selectedDate || undefined}
      />

      {/* Appointment Detail Modal */}
      <AppointmentDetailModal
        appointment={selectedAppointment}
        isOpen={!!selectedAppointment}
        onClose={() => setSelectedAppointment(null)}
        onUpdate={updateAppointment}
        onDelete={deleteAppointment}
        allAppointments={appointments}
      />

      {/* Appointment Setup Wizard */}
      <AppointmentSetupWizard
        isOpen={showSetupWizard}
        onClose={() => setShowSetupWizard(false)}
        onComplete={handleWizardComplete}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <>
          <div className="fixed inset-0 bg-pp-dark/80 backdrop-blur-sm z-[9998]" />
          <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 glass-effect rounded-[15px] z-[9999] max-w-sm mx-auto p-6">
            <h3 className="text-lg font-semibold text-white mb-2">
              {t('deleteAllTitle')}
            </h3>
            <p className="text-sm text-white/70 mb-6">
              {t('deleteAllDescription', { count: appointments.length })}
            </p>
            <div className="flex gap-2">
              <button
                className="flex-1 glass-effect py-2.5 rounded-xl text-white font-medium"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeletingAll}
              >
                {t('cancel')}
              </button>
              <button
                className="flex-1 bg-red-500 py-2.5 rounded-xl text-white font-medium flex items-center justify-center"
                onClick={handleDeleteAll}
                disabled={isDeletingAll}
              >
                {isDeletingAll ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  t('delete')
                )}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Wizard Prompt Modal (after delete) */}
      {showWizardPrompt && (
        <>
          <div className="fixed inset-0 bg-pp-dark/80 backdrop-blur-sm z-[9998]" />
          <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 glass-effect rounded-[15px] z-[9999] max-w-sm mx-auto p-6">
            <div className="text-center mb-4">
              <div className="w-12 h-12 bg-pp-orange/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <Wand2 className="w-6 h-6 text-pp-orange" />
              </div>
              <h3 className="text-lg font-semibold text-white">
                {t('wizardPrompt.title')}
              </h3>
            </div>
            <p className="text-sm text-white/70 mb-6 text-center">
              {t('wizardPrompt.description')}
            </p>
            <div className="flex gap-2">
              <button
                className="flex-1 glass-effect py-2.5 rounded-xl text-white font-medium"
                onClick={handleWizardPromptNo}
              >
                {t('wizardPrompt.no')}
              </button>
              <button
                className="flex-1 bg-pp-orange py-2.5 rounded-xl text-white font-medium"
                onClick={handleWizardPromptYes}
              >
                {t('wizardPrompt.yes')}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
